import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { parse } from 'node-html-parser';
import { HTMLElement } from 'node-html-parser';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import validator from 'validator';

import Component404 from '../../components/_molecules/Component404';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { getServerSideLang } from '../../utils/localisationUtils';

export default function CmsPage({
  htmlContent,
  headElements,
  pageTitle,
  coverImageUrl,
  pageDescription,
}: {
  htmlContent: string;
  headElements?: Array<{ tag: string; attrs: Record<string, string>; content?: string }>;
  pageTitle?: string;
  pageDescription?: string;
  coverImageUrl?: string;
}) {
  useEffect(() => {
    // Inject and execute Webflow scripts that need to run
    // Scripts in Next.js Head don't execute, so we inject them here
    const scriptElements = headElements?.filter(el => el.tag === 'script') || [];
    const injectedScripts: HTMLScriptElement[] = [];

    scriptElements.forEach(scriptData => {
      const script = document.createElement('script');

      // Copy all attributes
      Object.entries(scriptData.attrs).forEach(([key, value]) => {
        script.setAttribute(key, value);
      });

      // Add inline content if present
      if (scriptData.content) {
        script.textContent = scriptData.content;
      }

      // Append to head to execute
      document.head.appendChild(script);
      injectedScripts.push(script);
    });

    // This useEffect is solely for removing the webflow badge since the Webflow setting of removing the badge does not work
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        // If the mutation added nodes
        if (mutation.addedNodes) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutation.addedNodes.forEach((node: any) => {
            // If the node is an element and has the class 'w-webflow-badge' remove the element
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              node.classList.contains('w-webflow-badge')
            ) {
              node.remove();
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document, { childList: true, subtree: true });

    // Clean up
    return () => {
      observer.disconnect();
      // Remove injected scripts on cleanup
      injectedScripts.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [headElements]);
  if (!htmlContent) {
    return (
      <LandingPageLayout hideBlobs={true} disableHeadline={true}>
        <Component404 />
      </LandingPageLayout>
    );
  }

  return (
    <Wrapper>
      <LandingPageLayout
        imageUrl={coverImageUrl}
        title={pageTitle}
        subTitle={pageDescription}
        hideBlobs={true}
        disableHeadline={true}
      >
        <ErrorBoundary>
          <Head>
            {/* This is a hack to make sure the CMS properties are not overwriting the overflow */}
            <style>
              {`
                html {
                  height: unset !important;
                }
              `}
            </style>
            {/* Inject Webflow head elements (CSS links, styles, meta tags) */}
            {/* Scripts are injected via useEffect to ensure they execute properly */}
            {headElements?.map((el, idx) => {
              if (el.tag === 'link') {
                return <link key={idx} {...el.attrs} />;
              } else if (el.tag === 'style') {
                return (
                  <style key={idx} {...el.attrs}>
                    {el.content}
                  </style>
                );
              } else if (el.tag === 'meta') {
                return <meta key={idx} {...el.attrs} />;
              }
              // Scripts are handled in useEffect
              return null;
            })}
          </Head>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }}></div>
        </ErrorBoundary>
      </LandingPageLayout>
    </Wrapper>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  componentDidCatch(error: Error, _: React.ErrorInfo) {
    // You could log the error or perform other actions here.
    console.error(error);
  }

  render() {
    return this.props.children;
  }
}

const Wrapper = styled.div``;

const getPageTitle = (html: HTMLElement): string | undefined => {
  try {
    const elements = html.getElementsByTagName('title');
    const firstTitle = elements[0];
    const rawTitle = firstTitle.firstChild?.rawText;
    if (!rawTitle) return undefined;

    // Decode HTML entities like &#x27; to proper characters
    return rawTitle
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  } catch (e) {
    return undefined;
  }
};

const getPageDescription = (html: HTMLElement): string | undefined => {
  try {
    const head = html.querySelector('head');
    if (head) {
      const metas = head.querySelectorAll('meta');
      for (const meta of metas) {
        if (meta.getAttribute('name') === 'description') {
          const content = meta.getAttribute('content');
          if (content) return content;
        }
      }
    }

    const elements = html.getElementsByTagName('p');
    const firstP = elements[0];
    const rawDescription = firstP.firstChild?.rawText;
    if (!rawDescription) return undefined;

    return rawDescription
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&');
  } catch (e) {
    return undefined;
  }
};

const getFirstImageUrl = (html: HTMLElement): string | undefined => {
  try {
    const elements = html.getElementsByTagName('img');
    const first = elements[0];
    return first.getAttribute('src');
  } catch (e) {
    return undefined;
  }
};

const rewriteBlogPostLinks = (html: string): string => {
  //Links on the blog to individual blog posts are not automatically prefixed with /c
  //We need to do this manually
  const root = parse(html);
  const links = root.querySelectorAll('a');

  links.forEach(link => {
    const href = link.getAttribute('href');
    if (href?.startsWith('/blog-posts/')) {
      link.setAttribute('href', `/c${href}`);
    }
  });

  return root.toString();
};

const appendQueryParamsToInternalLinks = (
  html: string,
  queryParams: Record<string, string>,
): string => {
  // If there are no query parameters, return the HTML as is
  if (Object.keys(queryParams).length === 0) {
    return html;
  }

  const root = parse(html);
  const links = root.querySelectorAll('a');

  links.forEach(link => {
    const href = link.getAttribute('href');

    // Skip if no href or if it's an anchor link
    if (!href || href.startsWith('#')) {
      return;
    }

    try {
      // Parse the existing href to check if it already has query parameters
      const [path, existingQuery] = href.split('?');

      // Merge query parameters, with existing ones taking precedence
      const newParams = new URLSearchParams(queryParams);
      if (existingQuery) {
        const existingParams = new URLSearchParams(existingQuery);
        existingParams.forEach((value, key) => {
          newParams.set(key, value);
        });
      }
      const newQueryString = newParams.toString();
      const newHref = newQueryString ? `${path}?${newQueryString}` : path;
      link.setAttribute('href', newHref);
    } catch (e) {
      // If parsing fails, leave the link as is
      console.error('Error parsing link:', href, e);
    }
  });

  return root.toString();
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );

  // Set headers to allow Webflow resources to load in Instagram/Facebook in-app browsers
  // These browsers are stricter about external resources and may block external CSS/JS
  // Using a permissive policy specifically for Webflow content
  context.res.setHeader(
    'Content-Security-Policy',
    "default-src 'self' https:; " +
      "script-src 'self' 'unsafe-inline' 'unsafe-eval' https:; " +
      "style-src 'self' 'unsafe-inline' https:; " +
      "font-src 'self' https: data:; " +
      "img-src 'self' https: data: blob:; " +
      "connect-src 'self' https:; " +
      "frame-src 'self' https:; " +
      'frame-ancestors *;',
  );

  const params = context.params as { slug: string[] }; // Forcing type based on the route

  const postSlug = params.slug.join('/');
  // some secure measure to make sure this is a string so we not get injections
  if (!validator.isSlug(postSlug)) {
    return { props: { htmlContent: null } };
  }
  const postSlugValidated = params.slug
    .map(segment => encodeURIComponent(segment))
    .join('/');

  // Extract query parameters from the request (excluding the slug route param)
  const { slug, ...queryParamsObj } = context.query;

  const url = `https://curipod-cms.webflow.io/${postSlugValidated}`;
  const res = await fetch(url);
  const fullHtml =
    postSlug === 'blogs' || postSlug.includes('blog-posts')
      ? rewriteBlogPostLinks(await res.text())
      : await res.text();

  // Append query parameters to all internal links
  const updatedFullHtml = appendQueryParamsToInternalLinks(
    fullHtml,
    queryParamsObj as Record<string, string>,
  );

  const parsedcontent = parse(updatedFullHtml);
  const pageTitle = getPageTitle(parsedcontent);
  const coverImageUrl = getFirstImageUrl(parsedcontent) || null;
  const pageDescription = getPageDescription(parsedcontent) || '';

  // Extract head and body content separately
  // This ensures CSS/JS links are placed in the proper <head> of the Next.js document
  const headElement = parsedcontent.querySelector('head');
  const bodyElement = parsedcontent.querySelector('body');

  // Parse head elements (link, style, script, meta) for proper rendering
  const headElements: Array<{
    tag: string;
    attrs: Record<string, string>;
    content?: string;
  }> = [];
  if (headElement) {
    const links = headElement.querySelectorAll('link');
    const styles = headElement.querySelectorAll('style');
    const scripts = headElement.querySelectorAll('script');
    const metas = headElement.querySelectorAll('meta');

    links.forEach(link => {
      const attrs = link.attributes;
      if (Object.keys(attrs).length > 0) {
        headElements.push({ tag: 'link', attrs });
      }
    });

    styles.forEach(style => {
      const attrs = style.attributes;
      const content = style.innerHTML;
      if (content) {
        headElements.push({ tag: 'style', attrs, content });
      } else {
        headElements.push({ tag: 'style', attrs });
      }
    });

    scripts.forEach(script => {
      const attrs = script.attributes;
      const content = script.innerHTML;
      if (content) {
        headElements.push({ tag: 'script', attrs, content });
      } else {
        headElements.push({ tag: 'script', attrs });
      }
    });

    metas.forEach(meta => {
      const attrs = meta.attributes;
      if (Object.keys(attrs).length === 0) return;

      const name = attrs.name?.toLowerCase();
      const property = attrs.property?.toLowerCase();
      const isHandledByLayout =
        name === 'description' ||
        name === 'twitter:title' ||
        name === 'twitter:description' ||
        property === 'description' ||
        property === 'og:title' ||
        property === 'og:description';

      if (!isHandledByLayout) {
        headElements.push({ tag: 'meta', attrs });
      }
    });
  }
  const htmlContent = bodyElement?.innerHTML || fullHtml;
  // if we dont have the page the wordpress site will give back thair 404 page so we just check if the document contains that
  return {
    props: {
      htmlContent,
      headElements,
      pageTitle,
      coverImageUrl,
      pageDescription,
      _tolgeeSSR: {
        language: getServerSideLang(context),
      },
    },
  };
};
